import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AdminLayout from "../../../layouts/AdminLayout";
import Button from "../../../components/ui/Button";
import StatusBadge from "../../../components/ui/StatusBadge";
import Skeleton from "../../../components/ui/Skeleton";
import SearchInput from "../../../components/ui/SearchInput";
import SelectDropdown from "../../../components/ui/SelectDropdown";
import Modal from "../../../components/ui/Modal";
import { EmptyState } from "../../../components/ui/FeedbackStates";
import TablePagination from "../../../components/common/TablePagination";
import { Plus, RefreshCw, AlertTriangle } from "lucide-react";
import {
  cancelPreApprovedInvite,
  getMyPreApprovedInvites,
} from "../../../services/inviteService";
import styles from "./ResidentMyInvites.module.css";

const formatDateShort = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const formatTimeShort = (value) => {
  if (!value) return "-";
  if (typeof value === "string") {
    const match = value.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (match) return `${match[1].padStart(2, "0")}:${match[2]}`;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ResidentMyInvites = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [actionId, setActionId] = useState(null);
  const [cancelModalItem, setCancelModalItem] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const loadPage = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMyPreApprovedInvites();
      setRows(res.invites || []);
    } catch (error) {
      console.error("LOAD MY INVITES ERROR:", error);
      toast.error(error?.data?.message || error?.message || "Unable to load invites");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const filteredInvites = useMemo(() => {
    let result = rows || [];

    if (statusFilter !== "ALL") {
      result = result.filter(
        (r) => String(r.status || "").toUpperCase() === statusFilter
      );
    }

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      result = result.filter(
        (r) =>
          String(r.visitor_name || "").toLowerCase().includes(q) ||
          String(r.company_name || "").toLowerCase().includes(q) ||
          String(r.pass_code || "").toLowerCase().includes(q) ||
          String(r.title || "").toLowerCase().includes(q) ||
          String(r.mobile_number || "").includes(q)
      );
    }

    return result;
  }, [rows, statusFilter, searchTerm]);

  const paginatedInvites = useMemo(() => {
    const start = (page - 1) * limit;
    return filteredInvites.slice(start, start + limit);
  }, [filteredInvites, page, limit]);

  const handleCancelConfirm = async () => {
    if (!cancelModalItem) return;
    setActionId(cancelModalItem.id);
    try {
      await cancelPreApprovedInvite(cancelModalItem.id);
      toast.success("Invite cancelled successfully");
      setCancelModalItem(null);
      await loadPage();
    } catch (error) {
      console.error("CANCEL INVITE ERROR:", error);
      toast.error(error?.data?.message || error?.message || "Unable to cancel invite");
    } finally {
      setActionId(null);
    }
  };

  return (
    <AdminLayout title="My Invites">
      <div className={styles.headerRow}>
        <div>
          <h1 style={{ fontSize: "20px", fontWeight: 700, margin: "0 0 4px 0" }}>My Pre-Approved Invites</h1>
          <div className={styles.subtitle}>
            Manage and share digital gate passes for guests, cabs, and deliveries.
          </div>
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <Button variant="outline" size="sm" onClick={loadPage} title="Refresh">
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} style={{ marginRight: "4px" }} /> Refresh
          </Button>
          <Button onClick={() => navigate("/resident/invites/new")} size="sm">
            <Plus size={15} style={{ marginRight: "4px" }} /> Create Invite
          </Button>
        </div>
      </div>

      <div style={{ display: "flex", gap: "12px", alignItems: "center", marginBottom: "16px", flexWrap: "wrap" }}>
        <div style={{ width: "260px" }}>
          <SearchInput
            placeholder="Search visitor, phone, pass..."
            value={searchTerm}
            onChange={(val) => {
              setSearchTerm(val);
              setPage(1);
            }}
          />
        </div>
        <div style={{ width: "170px" }}>
          <SelectDropdown
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            options={[
              { label: "All Statuses", value: "ALL" },
              { label: "Active", value: "ACTIVE" },
              { label: "Used", value: "USED" },
              { label: "Expired", value: "EXPIRED" },
              { label: "Cancelled", value: "CANCELLED" },
              { label: "Blocked", value: "BLOCKED" },
            ]}
          />
        </div>
      </div>

      {loading ? (
        <div className={styles.tableCard} style={{ padding: "24px" }}>
          <Skeleton variant="card" count={3} />
        </div>
      ) : filteredInvites.length === 0 ? (
        <EmptyState
          title="No invites found"
          description={
            searchTerm || statusFilter !== "ALL"
              ? "No pre-approved passes match your search or filter."
              : "You have not generated any visitor or delivery passes yet."
          }
          action={
            !searchTerm && statusFilter === "ALL" ? (
              <Button onClick={() => navigate("/resident/invites/new")}>
                <Plus size={15} style={{ marginRight: "6px" }} /> Create First Invite
              </Button>
            ) : null
          }
        />
      ) : (
        <>
          <div className={styles.tableCard}>
            <table className={styles.table}>
              <thead className={styles.thead}>
                <tr>
                  <th className={styles.th}>Type</th>
                  <th className={styles.th}>Title</th>
                  <th className={styles.th}>Visitor</th>
                  <th className={styles.th}>Validity</th>
                  <th className={styles.th}>Pass Code</th>
                  <th className={styles.th}>Status</th>
                  <th className={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedInvites.map((row) => (
                  <tr key={row.id} className={styles.tbodyRow}>
                    <td className={styles.td}>
                      <span style={{ fontWeight: 600 }}>{row.invite_type}</span>
                      {row.invite_sub_type ? (
                        <div className={styles.visitorSub}>{row.invite_sub_type}</div>
                      ) : null}
                    </td>
                    <td className={styles.td}>{row.title || "-"}</td>
                    <td className={styles.td}>
                      <span style={{ fontWeight: 600 }}>{row.visitor_name || row.company_name || "-"}</span>
                      {row.mobile_number ? (
                        <div className={styles.visitorSub}>{row.mobile_number}</div>
                      ) : null}
                    </td>
                    <td className={[styles.td, styles.validity].join(" ")}>
                      {formatDateShort(row.valid_from)} → {formatDateShort(row.valid_to)}
                      <div className={styles.mutedLine}>
                        {formatTimeShort(row.start_time)} → {formatTimeShort(row.end_time)}
                      </div>
                    </td>
                    <td className={styles.td}>
                      <div className={styles.passCode} style={{ fontWeight: 700, color: "var(--color-primary)" }}>
                        {row.pass_code || "-"}
                      </div>
                    </td>
                    <td className={styles.td}>
                      <StatusBadge status={row.status} />
                    </td>
                    <td className={styles.td}>
                      <div className={styles.actionRow}>
                        <Button
                          variant="outline"
                          size="sm"
                          className={styles.smallButton}
                          onClick={() => navigate(`/resident/invites/${row.id}`)}
                        >
                          View
                        </Button>
                        {String(row.status || "").toLowerCase() === "active" ? (
                          <Button
                            variant="danger"
                            size="sm"
                            className={styles.smallButton}
                            disabled={actionId === row.id}
                            onClick={() => setCancelModalItem(row)}
                          >
                            Cancel
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <TablePagination
            totalItems={filteredInvites.length}
            page={page}
            limit={limit}
            onPageChange={setPage}
            onLimitChange={(newLimit) => {
              setLimit(newLimit);
              setPage(1);
            }}
            limitOptions={[5, 10, 25, 50, 100]}
          />
        </>
      )}

      {cancelModalItem && (
        <Modal
          isOpen={Boolean(cancelModalItem)}
          onClose={() => setCancelModalItem(null)}
          title="Cancel Pre-Approved Invite"
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "16px", padding: "8px 0" }}>
            <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
              <AlertTriangle size={24} color="#ef4444" style={{ flexShrink: 0, marginTop: "2px" }} />
              <div>
                <div style={{ fontWeight: 600, color: "var(--color-text)" }}>
                  Are you sure you want to cancel pass for {cancelModalItem.visitor_name || cancelModalItem.title || "this guest"}?
                </div>
                <div style={{ fontSize: "13px", color: "var(--color-text-muted)", marginTop: "4px" }}>
                  Pass Code: <strong>{cancelModalItem.pass_code}</strong> will be invalidated immediately. Security guards will decline entry.
                </div>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "8px" }}>
              <Button variant="outline" onClick={() => setCancelModalItem(null)}>
                Keep Pass
              </Button>
              <Button
                variant="danger"
                disabled={actionId === cancelModalItem.id}
                onClick={handleCancelConfirm}
              >
                {actionId === cancelModalItem.id ? "Cancelling..." : "Yes, Cancel Pass"}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </AdminLayout>
  );
};

export default ResidentMyInvites;
